import { useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, CheckCircle2, CheckCircle, Users, Lock } from "lucide-react";
import { toast } from "sonner";
import {
  createSupportTicket,
  newSubmissionKey,
  syncTicketToHelpDesk,
} from "@/lib/supportTicket";



type Route = "gathering" | "private";

interface AskAPersonFormProps {
  defaultRoute?: Route;
  onSubmitted?: (info: { questionId: string; route: Route }) => void;
  compact?: boolean;
}

// Unified "Ask a person" intake.
// Writes to congregation_questions with a chosen route.
// Private route also creates a linked support_tickets row and syncs to the
// existing help desk so a person can respond outside the gathering.
export function AskAPersonForm({
  defaultRoute = "gathering",
  onSubmitted,
  compact = false,
}: AskAPersonFormProps) {
  const { user } = useAuth();
  const [text, setText] = useState("");
  const [route, setRoute] = useState<Route>(defaultRoute);
  const [submitting, setSubmitting] = useState(false);
  const [submittedOk, setSubmittedOk] = useState(false);
  // Held across retries so a second attempt reuses the first attempt's ticket.
  const submissionKey = useRef<string>(newSubmissionKey());
  // Belt and braces next to the disabled button: blocks a double fire even if
  // the click lands before React repaints.
  const inFlight = useRef(false);

  const reset = () => {
    setText("");
    setSubmittedOk(false);
    setRoute(defaultRoute);
    submissionKey.current = newSubmissionKey();
  };

  const submit = async () => {
    if (!user?.id) return;
    if (inFlight.current) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    inFlight.current = true;
    setSubmitting(true);
    try {
      // 1. If private, create (or reuse) the linked ticket so we can store its id.
      let ticketId: string | null = null;
      let profile: { full_name: string | null; email: string | null } | null = null;
      const subject = trimmed.length > 80 ? `${trimmed.slice(0, 77)}...` : trimmed;

      if (route === "private") {
        const { data: p } = await supabase
          .from("profiles")
          .select("full_name, email")
          .eq("id", user.id)
          .maybeSingle();
        profile = p ?? null;

        const ticket = await createSupportTicket({
          userId: user.id,
          subject,
          message: trimmed,
          idempotencyKey: submissionKey.current,
        });
        ticketId = ticket.id;
      }

      // 2. Always insert into congregation_questions with the chosen route.
      const { data: inserted, error } = await supabase
        .from("congregation_questions")
        .insert({
          user_id: user.id,
          question_text: trimmed,
          route,
          ticket_id: ticketId,
        } as any)
        .select("id")
        .single();
      if (error) throw error;

      // 3. Only now hand it to the help desk. If step 2 fails the member
      // retries, and no half-saved question has reached a person yet.
      if (route === "private" && ticketId) {
        syncTicketToHelpDesk({
          ticketId,
          subject,
          message: trimmed,
          userEmail: profile?.email || user.email || "",
          userName: profile?.full_name || "Member",
          category: "General",
          priority: "medium",
        });
      }

      setSubmittedOk(true);
      onSubmitted?.({ questionId: inserted.id, route });

      // 4. Non-blocking Ontraport notify
      supabase.functions
        .invoke("notify-question-submitted", { body: { question_id: inserted.id } })
        .catch((e) => console.error("notify-question-submitted failed:", e));
    } catch (err: any) {
      console.error("ask-a-person submit failed:", err);
      toast.error("Your question didn't save", {
        description: "Try once more in a moment. If it keeps happening, let us know.",
      });
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  };


  if (submittedOk) {
    return (
      <div className="rounded-md border border-green-600/30 bg-green-50 p-4 flex items-start gap-2">
        <CheckCircle2 className="w-5 h-5 text-green-700 mt-0.5" />
        <div className="text-sm text-green-900 space-y-2">
          <p>
            {route === "gathering"
              ? "Received. We will bring this to the weekly congregation and send you the answer."
              : "Received. A person will follow up with you privately."}
          </p>
          <Button variant="outline" size="sm" onClick={reset}>
            Ask another
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={compact ? "space-y-3" : "space-y-4"}>
      <div className="space-y-2">
        <Label htmlFor="ask-text" className="text-sm font-medium">
          What would you like to ask?
        </Label>
        <Textarea
          id="ask-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={compact ? 4 : 5}
          placeholder="Share the question, the situation behind it, and anything worth knowing."
        />
      </div>

      <div className="space-y-2">
        <Label className="text-sm font-medium" id="ask-route-label">
          How would you like it answered?
        </Label>
        <RouteCards value={route} onChange={setRoute} />
      </div>


      <div className="flex justify-end">
        {(() => { const canSend = !submitting && text.trim().length > 0; return (
        <Button onClick={submit} disabled={!canSend} className={`transition-colors ${canSend ? "bg-[hsl(var(--deep-green))] text-[hsl(var(--deep-green-foreground))] hover:bg-[hsl(var(--deep-green-hover))]" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>

          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Sending...
            </>
          ) : route === "gathering" ? (
            "Send to the congregation"
          ) : (
            "Send privately"
          )}
        </Button>
        ); })()}
      </div>
    </div>
  );
}

type RouteOption = {
  value: Route;
  title: string;
  helper: string;
  Icon: typeof Users;
};

const ROUTE_OPTIONS: RouteOption[] = [
  {
    value: "gathering",
    title: "Answer this at the weekly congregation",
    helper: "It helps everyone, and you get the answer either way.",
    Icon: Users,
  },
  {
    value: "private",
    title: "Answer this privately",
    helper: "For anything sensitive or specific to your situation.",
    Icon: Lock,
  },
];

function RouteCards({
  value,
  onChange,
}: {
  value: Route;
  onChange: (v: Route) => void;
}) {
  const btnRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const move = (dir: 1 | -1, from: number) => {
    const next = (from + dir + ROUTE_OPTIONS.length) % ROUTE_OPTIONS.length;
    onChange(ROUTE_OPTIONS[next].value);
    btnRefs.current[next]?.focus();
  };

  return (
    <div
      role="radiogroup"
      aria-label="How would you like it answered?"
      className="grid gap-2"
    >
      {ROUTE_OPTIONS.map((opt, i) => {
        const selected = value === opt.value;
        const { Icon } = opt;
        return (
          <button
            key={opt.value}
            ref={(el) => (btnRefs.current[i] = el)}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(opt.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown" || e.key === "ArrowRight") {
                e.preventDefault();
                move(1, i);
              } else if (e.key === "ArrowUp" || e.key === "ArrowLeft") {
                e.preventDefault();
                move(-1, i);
              } else if (e.key === " " || e.key === "Enter") {
                e.preventDefault();
                onChange(opt.value);
              }
            }}
            className={`relative w-full text-left flex items-start gap-3 rounded-md border p-3 min-h-11 transition-all duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
              selected
                ? "border-gold bg-gold/10"
                : "border-border bg-transparent hover:bg-muted/40"
            }`}
          >
            <div className="min-w-0 flex-1 pr-6">
              <div className="flex items-center gap-1.5 text-sm font-medium">
                <Icon className="w-4 h-4 text-gold shrink-0" />
                <span className="truncate">{opt.title}</span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">{opt.helper}</p>
            </div>
            {selected && (
              <CheckCircle
                className="w-5 h-5 text-gold fill-gold/20 absolute top-3 right-3"
                aria-hidden="true"
              />
            )}
          </button>
        );
      })}
    </div>
  );
}

